import {CommonModule} from '@angular/common';
import {Component, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ProfileSettingsService} from '@gatewee/services/profile-settings.service';
import {
  NotificationChannelSettings,
  defaultNotificationChannelSettings,
} from '@gatewee/types/user-setting';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {catchError, finalize, of} from 'rxjs';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';
import {GateweeCheckboxComponent} from '../gatewee-checkbox/gatewee-checkbox.component';
import {GateweeSkeletonComponent} from '../../v1/gatewee-skeleton/gatewee-skeleton.component';

@Component({
  selector: 'gatewee-profile-notification-preferences',
  templateUrl: './gatewee-profile-notification-preferences.component.html',
  styleUrls: ['./gatewee-profile-notification-preferences.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeCheckboxComponent,
    GateweeSkeletonComponent,
  ],
})
export class GateweeProfileNotificationPreferencesComponent implements OnInit {
  settings: NotificationChannelSettings = {
    ...defaultNotificationChannelSettings,
  };
  loading = true;
  saving = false;
  loadError = false;

  private readonly profileSettings = inject(ProfileSettingsService);
  private readonly alerts = inject(TuiAlertService);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    this.loadSettings();
  }

  save(): void {
    if (this.saving || this.loading) {
      return;
    }

    this.saving = true;
    this.profileSettings
      .saveNotificationSettings(this.settings)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: settings => {
          this.settings = settings;
          this.alerts
            .open(
              this.translate.instant('features.profile.notifications.success'),
              {
                label: this.translate.instant('common.success'),
                appearance: 'success',
              },
            )
            .subscribe();
        },
        error: err => {
          this.alerts
            .open(this.resolveErrorMessage(err), {
              label: this.translate.instant('common.error'),
              appearance: 'error',
            })
            .subscribe();
        },
      });
  }

  private loadSettings(): void {
    this.loading = true;
    this.loadError = false;
    this.profileSettings
      .getNotificationSettings()
      .pipe(
        catchError(() => {
          this.loadError = true;
          return of(this.profileSettings.getDefaultNotificationSettings());
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe(settings => {
        this.settings = settings;
      });
  }

  private resolveErrorMessage(error: unknown): string {
    if (
      error &&
      typeof error === 'object' &&
      'error' in error &&
      (error as {error?: {message?: string}}).error?.message
    ) {
      return String((error as {error: {message: string}}).error.message);
    }
    return this.translate.instant('common.errors.unknown');
  }
}
